import { Schema } from "effect";

import { RuntimeNamespaceId } from "./development-identifiers.ts";
import { WorkflowSourceStep } from "./workflow-detection.ts";

export const WorkflowStepStatus = /* @__PURE__ */ (() => Schema.Literals(["pending", "running", "complete", "errored"]))();
export type WorkflowStepStatus = typeof WorkflowStepStatus.Type;
export const WorkflowRuntimeStatus = /* @__PURE__ */ (() => Schema.Literals(["queued", "running", "paused", "errored", "terminated", "complete", "waiting", "waitingForPause", "rollingBack", "unknown"]))();
export type WorkflowRuntimeStatus = typeof WorkflowRuntimeStatus.Type;

export const WorkflowControlAction = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  action: Schema.Literals(["pause", "resume", "terminate"]).pipe(Schema.mutableKey),
}), Schema.Struct({
  action: Schema.Literal("restart").pipe(Schema.mutableKey),
  from: Schema.Union([Schema.String, Schema.Struct({
    name: Schema.String.pipe(Schema.mutableKey),
    count: Schema.Number.pipe(Schema.mutableKey),
    type: Schema.String.pipe(Schema.mutableKey),
  })]).pipe(Schema.optional, Schema.mutableKey),
  stepId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  params: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
}), Schema.Struct({
  action: Schema.Literal("sendEvent").pipe(Schema.mutableKey),
  type: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  name: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  event: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  payload: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
})]))();
export type WorkflowControlAction = typeof WorkflowControlAction.Type;

export const WorkflowRoutingMetadata = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  workerId: Schema.String.pipe(Schema.mutableKey),
  anchorId: Schema.String.pipe(Schema.mutableKey),
  registryId: Schema.String.pipe(Schema.mutableKey),
  workspaceId: Schema.String.pipe(Schema.mutableKey),
  workerName: Schema.String.pipe(Schema.mutableKey),
  sessionId: Schema.String.pipe(Schema.mutableKey),
  className: Schema.String.pipe(Schema.mutableKey),
  bindingName: Schema.String.pipe(Schema.mutableKey),
  runtimeNamespaceId: Schema.Union([RuntimeNamespaceId, Schema.String]).pipe(Schema.optional, Schema.mutableKey),
  /** Per-create logical id carried inside the dispatcher envelope. */
  instanceId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
}), [Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown))]))();
export type WorkflowRoutingMetadata = typeof WorkflowRoutingMetadata.Type;

export const WorkflowDefinitionRecord = /* @__PURE__ */ (() => Schema.Struct({
  className: Schema.String.pipe(Schema.mutableKey),
  file: Schema.String.pipe(Schema.mutableKey),
  bindingNames: Schema.Array(Schema.String).pipe(Schema.mutable, Schema.mutableKey),
  steps: Schema.Array(WorkflowSourceStep).pipe(Schema.mutable, Schema.mutableKey),
  routing: WorkflowRoutingMetadata.pipe(Schema.mutableKey),
  updatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type WorkflowDefinitionRecord = typeof WorkflowDefinitionRecord.Type;

export const WorkflowStepRecord = /* @__PURE__ */ (() => Schema.Struct({
  ...WorkflowSourceStep.fields,
  token: Schema.String.pipe(Schema.mutableKey),
  count: Schema.Number.pipe(Schema.mutableKey),
  status: WorkflowStepStatus.pipe(Schema.mutableKey),
  startedAt: Schema.Number.pipe(Schema.mutableKey),
  completedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  output: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  error: Schema.Struct({
    name: Schema.String.pipe(Schema.mutableKey),
    message: Schema.String.pipe(Schema.mutableKey),
  }).pipe(Schema.optional, Schema.mutableKey),
}))();
export type WorkflowStepRecord = typeof WorkflowStepRecord.Type;

export const WorkflowInstanceRecord = /* @__PURE__ */ (() => Schema.Struct({
  /** Logical id exposed to user code and the Workerstation API. */
  id: Schema.String.pipe(Schema.mutableKey),
  /** Cryptographically scoped id used by the shared platform Workflow. */
  physicalId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  className: Schema.String.pipe(Schema.mutableKey),
  bindingName: Schema.String.pipe(Schema.mutableKey),
  status: WorkflowRuntimeStatus.pipe(Schema.mutableKey),
  params: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  output: Schema.Unknown.pipe(Schema.optional, Schema.mutableKey),
  error: Schema.Struct({
    name: Schema.String.pipe(Schema.mutableKey),
    message: Schema.String.pipe(Schema.mutableKey),
  }).pipe(Schema.optional, Schema.mutableKey),
  steps: Schema.Array(WorkflowStepRecord).pipe(Schema.mutable, Schema.mutableKey),
  createdAt: Schema.Number.pipe(Schema.mutableKey),
  updatedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type WorkflowInstanceRecord = typeof WorkflowInstanceRecord.Type;

export const WorkflowWorkspaceSnapshot = /* @__PURE__ */ (() => Schema.Struct({
  definitions: Schema.Array(WorkflowDefinitionRecord).pipe(Schema.mutable, Schema.mutableKey),
  instances: Schema.Array(WorkflowInstanceRecord).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type WorkflowWorkspaceSnapshot = typeof WorkflowWorkspaceSnapshot.Type;
